import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Tag, Space, Tooltip, Button } from "antd"
import type React from "react"
import { useTableScroll } from "@/hooks/useTableScroll"
import { WEB_URL } from "@/configs/config"
import "@/styles/table.full.css"
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SoundOutlined,
} from "@ant-design/icons"
import { formatDateTime } from "@/utils/dateUtils"
import { simplifyPos } from "@/utils/utils"
import { TABLE_ACTION_COLUMN_WIDTH } from "@/constants/table"

interface HeadwordTableProps {
  data: any[]
  loading: boolean
  onView: (id: number) => void
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  pagination?: TableProps["pagination"]
}

const getPosColor = (pos: string) => {
  const normalized = pos.trim().toLowerCase()
  if (normalized.startsWith("noun")) {
    return "blue"
  }
  if (normalized.startsWith("verb")) {
    return "gold"
  }
  if (normalized.startsWith("adj")) {
    return "green"
  }
  if (normalized.startsWith("adv")) {
    return "purple"
  }
  return "default"
}

const HeadwordTable: React.FC<HeadwordTableProps> = ({
  data,
  loading,
  onView,
  selectedRowKeys,
  onSelectionChange,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 80,
      align: "center",
    },
    {
      title: "Từ vựng",
      dataIndex: "word",
      key: "word",
      width: 120,
      render: (value: string, record: any) => (
        <a href={`${WEB_URL}/en-vi/dictionary/${record.slug || value}`} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      ),
    },
    {
      title: "Phát âm / IPA",
      key: "pronunciation",
      width: 230,
      render: (_: any, record: any) => {
        const usIpa = record.pronunciation?.us
        const ukIpa = record.pronunciation?.uk
        const usAudio = record.audio?.us
        const ukAudio = record.audio?.uk

        const playAudio = (url) => {
          const audio = new Audio(url)
          audio.play().catch((err) => console.error("Lỗi phát âm thanh:", err))
        }

        return (
          <Space direction="vertical" size={2}>
            {usIpa && (
              <Space>
                <Tag color="blue">US</Tag>
                <span className="font-mono">{usIpa}</span>
                <SoundOutlined
                  className="cursor-pointer text-blue-500 hover:text-blue-600"
                  onClick={() => playAudio(usAudio)}
                />
              </Space>
            )}
            {ukIpa && (
              <Space>
                <Tag color="red" className="w-8 text-center">
                  UK
                </Tag>
                <span className="font-mono">{ukIpa}</span>
                <SoundOutlined
                  className="cursor-pointer text-red-500 hover:text-red-600"
                  onClick={() => playAudio(ukAudio)}
                />
              </Space>
            )}
          </Space>
        )
      },
    },
    {
      title: "Từ loại",
      dataIndex: "part_of_speech",
      key: "part_of_speech",
      width: 120,
      render: (pos: string[]) => {
        if (!pos || !Array.isArray(pos)) return "_"
        return (
          <Space wrap size={[4, 4]}>
            {pos.map((item) => (
              <Tag color={getPosColor(item)} key={item}>
                {item}
              </Tag>
            ))}
          </Space>
        )
      },
    },
    {
      title: "Nghĩa",
      key: "meaning",
      ellipsis: { showTitle: true },
      render: (_: any, record: any) => {
        const sense = record.senses?.[0]
        if (sense) {
          return `(${simplifyPos(sense.part_of_speech)}) ${sense.meaning}`
        }
        return "_"
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center",
      render: (status: number) => {
        const presets = {
          [-1]: { status: "default", icon: <ClockCircleOutlined />, label: "Không xác định" },
          [1]: { status: "success", icon: <CheckCircleOutlined />, label: "Đã xuất bản" },
          [10]: { status: "error", icon: <CloseCircleOutlined />, label: "Đã hủy" },
        } as const
        const preset = presets[status as keyof typeof presets] || {
          status: "default",
          icon: <ClockCircleOutlined />,
          label: "Khởi tạo",
        }
        return (
          <Tag icon={preset.icon} color={preset.status}>
            {preset.label}
          </Tag>
        )
      },
    },
    {
      title: "Ngày cập nhật",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 130,
      align: "center",
      render: (value: string) => formatDateTime(value, "dd/mm/yy hh:mm") || "_",
    },
    {
      title: "Thao tác",
      key: "action",
      width: TABLE_ACTION_COLUMN_WIDTH,
      align: "center",
      fixed: "right",
      render: (_: string, record: any) => (
        <Space size="small" wrap={false}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined className="text-base! text-teal-500! hover:text-teal-600!" />}
              onClick={() => onView(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

HeadwordTable.displayName = "HeadwordTable"

export default HeadwordTable
